/**
 * SpatialGrid - Grid-based spatial partitioning for collision detection
 *
 * Reduces collision checks from O(n²) to O(n) by dividing the screen
 * into cells and only checking entities in nearby cells.
 *
 * Performance:
 * - With 100 bullets and 50 enemies: ~500 checks instead of 5,000
 * - Cell size of 60px balances granularity vs overhead
 */

import { Enemy } from '../entities/Enemy';

/** Entity that can be stored in the grid */
export interface GridEntity {
  x: number;
  y: number;
  getIsAlive?: () => boolean;
  getCollisionRadius?: () => number;
}

export class SpatialGrid {
  /** Cell size in pixels */
  private readonly cellSize: number;

  /** The grid cells - Map of "x,y" string to array of entities */
  private cells: Map<string, Enemy[]> = new Map();

  /** Threshold for bypassing grid (not worth overhead at low counts) */
  private static readonly BYPASS_THRESHOLD = 30;

  constructor(cellSize: number = 60) {
    this.cellSize = cellSize;
  }

  /**
   * Clears all entities from the grid
   * Call at start of each frame before inserting
   */
  clear(): void {
    this.cells.clear();
  }

  /**
   * Gets the cell key for a position
   */
  private getCellKey(x: number, y: number): string {
    const cellX = Math.floor(x / this.cellSize);
    const cellY = Math.floor(y / this.cellSize);
    return `${cellX},${cellY}`;
  }

  /**
   * Gets the cell coordinates for a position
   */
  private getCellCoords(x: number, y: number): { cellX: number; cellY: number } {
    return {
      cellX: Math.floor(x / this.cellSize),
      cellY: Math.floor(y / this.cellSize),
    };
  }

  /**
   * Inserts an entity into the grid
   */
  insert(entity: Enemy): void {
    const key = this.getCellKey(entity.x, entity.y);

    let cell = this.cells.get(key);
    if (!cell) {
      cell = [];
      this.cells.set(key, cell);
    }
    cell.push(entity);
  }

  /**
   * Inserts multiple entities into the grid
   */
  insertAll(entities: Enemy[]): void {
    for (const entity of entities) {
      if (entity.getIsAlive()) {
        this.insert(entity);
      }
    }
  }

  /**
   * Gets all entities near a position (checks 3x3 cells around point)
   * This catches entities even if they're at the edge of adjacent cells
   */
  getNearby(x: number, y: number): Enemy[] {
    const { cellX, cellY } = this.getCellCoords(x, y);
    const result: Enemy[] = [];

    // Check 3x3 grid of cells around the position
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const key = `${cellX + dx},${cellY + dy}`;
        const cell = this.cells.get(key);
        if (cell) {
          result.push(...cell);
        }
      }
    }

    return result;
  }

  /**
   * Gets entities within a specific radius (more precise than getNearby)
   * Uses squared distance to avoid sqrt
   */
  getInRadius(x: number, y: number, radius: number): Enemy[] {
    const nearby = this.getNearby(x, y);
    const radiusSq = radius * radius;

    return nearby.filter(entity => {
      const dx = entity.x - x;
      const dy = entity.y - y;
      return dx * dx + dy * dy <= radiusSq;
    });
  }

  /**
   * Checks if grid should be used based on entity count
   * Returns false if overhead isn't worth it
   */
  static shouldUseGrid(bulletCount: number): boolean {
    return bulletCount >= SpatialGrid.BYPASS_THRESHOLD;
  }

  /**
   * Gets number of occupied cells (for debugging)
   */
  getOccupiedCellCount(): number {
    return this.cells.size;
  }

  /**
   * Gets total entity count across all cells (for debugging)
   */
  getTotalEntityCount(): number {
    let count = 0;
    for (const cell of this.cells.values()) {
      count += cell.length;
    }
    return count;
  }

  /**
   * Gets grid statistics (for debugging/performance monitoring)
   */
  getStats(): {
    occupiedCells: number;
    totalEntities: number;
    avgEntitiesPerCell: number;
    cellSize: number;
  } {
    const occupiedCells = this.cells.size;
    const totalEntities = this.getTotalEntityCount();
    return {
      occupiedCells,
      totalEntities,
      avgEntitiesPerCell: occupiedCells > 0 ? totalEntities / occupiedCells : 0,
      cellSize: this.cellSize,
    };
  }
}
